import { useState } from "react";
import "./WebsiteBuilderPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useWebsite } from "../../../hooks/useWebsite";
import {
  createTestimonialAsync,
  createWebsiteMediaAsync,
  createWebsiteStatAsync,
  deleteTestimonialAsync,
  deleteWebsiteMediaAsync,
  deleteWebsiteStatAsync,
  updateTestimonialAsync,
  updateWebsiteMediaAsync,
  updateWebsiteSettingsAsync,
  updateWebsiteStatAsync,
} from "../../../api/WebsiteApi";
import {
  Testimonial,
  UpdateTestimonialRequest,
  UpdateWebsiteMediaRequest,
  UpdateWebsiteStatRequest,
  WebsiteMedia,
  WebsiteMediaType,
  WebsiteStat,
} from "../../../models/WebsiteModels";
import { Service } from "../../../models/WebsiteModels";
import { Constants } from "../../../constants/constants";
import { resolveImageUrl } from "../../../utils/ImageUrl";
import MediaModal, { MediaFormValues } from "./MediaModal";
import StatModal, { StatFormValues } from "./StatModal";
import TestimonialModal, {
  TestimonialFormValues,
} from "./TestimonialModal";
import WebsiteSettingsModal, {
  WebsiteSettingsFormValues,
} from "./WebsiteSettingsModal";
import Toast from "../../../components/Toast";
import { OrgForms } from "../../../config/forms";

type ModalState<T> = {
  open: boolean;
  mode: "create" | "edit";
  item: T | null;
};

const initialModalState = <T,>(): ModalState<T> => ({
  open: false,
  mode: "create",
  item: null,
});

const isStatIconImage = (icon: string): boolean => {
  if (/^data:image\//i.test(icon)) return true;
  const path = icon.split("?")[0].split("#")[0];
  if (/\.(png|jpe?g|gif|svg|webp|ico|bmp|avif)$/i.test(path)) return true;
  if (/^https?:\/\//i.test(icon)) return true;
  return false;
};

const WebsiteBuilderPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const slug = user?.activeOrganization?.slug;
  const canCreateContent = hasPrivilege(OrgForms.WebsiteBuilder, "canCreate");
  const canEditContent = hasPrivilege(OrgForms.WebsiteBuilder, "canEdit");
  const canDeleteContent = hasPrivilege(OrgForms.WebsiteBuilder, "canDelete");

  const {
    website,
    settings,
    promoImages,
    beforeAfter,
    stats,
    testimonials,
    loading,
    error,
    reload,
    addPromo,
    updatePromo,
    removePromo,
    addBeforeAfter,
    updateBeforeAfter,
    removeBeforeAfter,
    addStat,
    updateStat,
    removeStat,
    addTestimonial,
    updateTestimonial,
    removeTestimonial,
  } = useWebsite(organizationId, slug);

  const [mediaModal, setMediaModal] = useState<ModalState<WebsiteMedia>>(
    initialModalState<WebsiteMedia>(),
  );
  const [mediaType, setMediaType] = useState<WebsiteMediaType>("promo");
  const [statModal, setStatModal] = useState<ModalState<WebsiteStat>>(
    initialModalState<WebsiteStat>(),
  );
  const [testimonialModal, setTestimonialModal] = useState<
    ModalState<Testimonial>
  >(initialModalState<Testimonial>());
  const [settingsModal, setSettingsModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const openPreviewWebsite = () => {
    if (!slug) {
      setToast({ message: "No website slug available", type: "error" });
      return;
    }
    window.open(`/${slug}`, "_blank", "noopener,noreferrer");
  };

  const openMediaModal = (
    mode: "create" | "edit",
    type: WebsiteMediaType,
    item: WebsiteMedia | null,
  ) => {
    setMediaType(type);
    setMediaModal({ open: true, mode, item });
  };

  const openStatModal = (
    mode: "create" | "edit",
    item: WebsiteStat | null,
  ) => {
    setStatModal({ open: true, mode, item });
  };

  const openTestimonialModal = (
    mode: "create" | "edit",
    item: Testimonial | null,
  ) => {
    setTestimonialModal({ open: true, mode, item });
  };

  const handleMediaSubmit = async (form: MediaFormValues) => {
    setSubmitting(true);
    try {
      if (mediaModal.mode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const res = await createWebsiteMediaAsync(token, {
          organizationId,
          mediaType,
          title: form.title || undefined,
          description: form.description || undefined,
          imageFile: mediaType === "promo" ? form.imageFile : null,
          beforeImageFile:
            mediaType === "before_after" ? form.beforeImageFile : null,
          afterImageFile:
            mediaType === "before_after" ? form.afterImageFile : null,
          sortOrder: form.sortOrder,
          isActive: true,
        });
        if (res.success) {
          if (res.result) {
            if (mediaType === "promo") addPromo(res.result);
            else addBeforeAfter(res.result);
          }
          setMediaModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({
            message:
              mediaType === "promo"
                ? "Promo image added successfully!"
                : "Before / After added successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to add media",
            type: "error",
          });
        }
        return;
      }

      if (mediaModal.mode === "edit" && mediaModal.item) {
        const payload: UpdateWebsiteMediaRequest = {
          title: form.title || undefined,
          description: form.description || undefined,
          imageUrl: mediaModal.item.imageUrl ?? undefined,
          beforeImageUrl: mediaModal.item.beforeImageUrl ?? undefined,
          afterImageUrl: mediaModal.item.afterImageUrl ?? undefined,
          sortOrder: form.sortOrder,
          isActive: mediaModal.item.isActive,
          imageFile: form.imageFile,
          beforeImageFile: form.beforeImageFile,
          afterImageFile: form.afterImageFile,
        };
        const res = await updateWebsiteMediaAsync(
          token,
          mediaModal.item.id,
          payload,
        );
        if (res.success) {
          if (res.result) {
            if (mediaType === "promo") updatePromo(res.result);
            else updateBeforeAfter(res.result);
          }
          setMediaModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({
            message: "Media updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update media",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatSubmit = async (form: StatFormValues) => {
    setSubmitting(true);
    try {
      if (statModal.mode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const res = await createWebsiteStatAsync(token, {
          organizationId,
          iconFile: form.iconFile,
          value: form.value,
          label: form.label,
          sortOrder: 0,
          isActive: true,
        });
        if (res.success) {
          if (res.result) addStat(res.result);
          setStatModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({ message: "Stat added successfully!", type: "success" });
        } else {
          setToast({
            message: res.message || "Failed to add stat",
            type: "error",
          });
        }
        return;
      }

      if (statModal.mode === "edit" && statModal.item) {
        const payload: UpdateWebsiteStatRequest = {
          value: form.value,
          label: form.label,
          iconFile: form.iconFile,
        };
        const res = await updateWebsiteStatAsync(
          token,
          statModal.item.id,
          payload,
        );
        if (res.success) {
          if (res.result) updateStat(res.result);
          setStatModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({ message: "Stat updated successfully!", type: "success" });
        } else {
          setToast({
            message: res.message || "Failed to update stat",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestimonialSubmit = async (form: TestimonialFormValues) => {
    setSubmitting(true);
    try {
      if (testimonialModal.mode === "create") {
        if (!organizationId) {
          setToast({ message: "No active organization", type: "error" });
          return;
        }
        const res = await createTestimonialAsync(token, {
          organizationId,
          clientName: form.clientName,
          clientTitle: form.clientTitle || undefined,
          clientPhotoUrl: form.clientPhotoUrl || undefined,
          rating: form.rating ? Number(form.rating) : undefined,
          reviewText: form.reviewText,
          sortOrder: 0,
          isFeatured: form.isFeatured,
          isActive: true,
        });
        if (res.success) {
          if (res.result) addTestimonial(res.result);
          setTestimonialModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({
            message: "Testimonial added successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to add testimonial",
            type: "error",
          });
        }
        return;
      }

      if (testimonialModal.mode === "edit" && testimonialModal.item) {
        const payload: UpdateTestimonialRequest = {
          clientName: form.clientName,
          clientTitle: form.clientTitle || undefined,
          clientPhotoUrl: form.clientPhotoUrl || undefined,
          rating: form.rating ? Number(form.rating) : undefined,
          reviewText: form.reviewText,
          isFeatured: form.isFeatured,
        };
        const res = await updateTestimonialAsync(
          token,
          testimonialModal.item.id,
          payload,
        );
        if (res.success) {
          if (res.result) updateTestimonial(res.result);
          setTestimonialModal((prev) => ({ ...prev, open: false }));
          await reload();
          setToast({
            message: "Testimonial updated successfully!",
            type: "success",
          });
        } else {
          setToast({
            message: res.message || "Failed to update testimonial",
            type: "error",
          });
        }
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSettingsSubmit = async (form: WebsiteSettingsFormValues) => {
    if (!settings?.id) {
      setToast({
        message: "No website settings found for this organization.",
        type: "error",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await updateWebsiteSettingsAsync(token, settings.id, {
        themeNumber: form.themeNumber ? Number(form.themeNumber) : undefined,
        brandColor: form.brandColor || undefined,
        appointmentImageUrl: settings.appointmentImageUrl ?? undefined,
        taglineImageUrl: settings.taglineImageUrl ?? undefined,
        isActive: form.isActive,
        appointmentImageFile: form.appointmentImageFile || null,
        taglineImageFile: form.taglineImageFile || null,
      });
      if (res.success) {
        setSettingsModal(false);
        await reload();
        setToast({ message: "Website settings saved!", type: "success" });
      } else {
        setToast({
          message: res.message || "Failed to save website settings",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to save website settings. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMedia = async (item: WebsiteMedia, type: WebsiteMediaType) => {
    setSubmitting(true);
    try {
      const res = await deleteWebsiteMediaAsync(token, item.id);
      if (res.success) {
        if (type === "promo") removePromo(item.id);
        else removeBeforeAfter(item.id);
        await reload();
        setToast({ message: "Media deleted successfully!", type: "success" });
      } else {
        setToast({ message: res.message || "Failed to delete media", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to delete media. Please try again.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStat = async (item: WebsiteStat) => {
    setSubmitting(true);
    try {
      const res = await deleteWebsiteStatAsync(token, item.id);
      if (res.success) {
        removeStat(item.id);
        await reload();
        setToast({ message: "Stat deleted successfully!", type: "success" });
      } else {
        setToast({ message: res.message || "Failed to delete stat", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to delete stat. Please try again.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTestimonial = async (item: Testimonial) => {
    setSubmitting(true);
    try {
      const res = await deleteTestimonialAsync(token, item.id);
      if (res.success) {
        removeTestimonial(item.id);
        await reload();
        setToast({ message: "Testimonial deleted successfully!", type: "success" });
      } else {
        setToast({ message: res.message || "Failed to delete testimonial", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to delete testimonial. Please try again.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (message: string, action: () => void) => {
    if (window.confirm(message)) action();
  };

  const renderImage = (src?: string | null, alt?: string | null) =>
    src ? (
      <img
        className="wb-img"
        src={resolveImageUrl(src)}
        alt={alt ?? "media"}
      />
    ) : (
      <div className="wb-img wb-img--empty">No image</div>
    );

  const renderMediaRow = (item: WebsiteMedia, type: WebsiteMediaType) => {
    const isPromo = type === "promo";
    return (
      <div key={item.id} className="wb-card">
        {isPromo
          ? renderImage(item.imageUrl, item.title)
          : (
              <div className="wb-beforeafter">
                {renderImage(item.beforeImageUrl, `${item.title ?? ""} before`)}
                {renderImage(item.afterImageUrl, `${item.title ?? ""} after`)}
              </div>
            )}
        <div className="wb-card__meta">
          <p className="wb-card__title">{item.title || (isPromo ? "Promo image" : "Before / After")}</p>
          {!item.isActive && <span className="wb-badge wb-badge--inactive">Inactive</span>}
        </div>
        <div className="wb-card__actions">
          {canEditContent && (
            <button
              className="wb-btn wb-btn--small"
              onClick={() => openMediaModal("edit", type, item)}
            >
              Edit
            </button>
          )}
          {canDeleteContent && (
            <button
              className="wb-btn wb-btn--small wb-btn--danger"
              onClick={() =>
                confirmDelete(`Delete "${item.title ?? "this media"}"?`, () =>
                  handleDeleteMedia(item, type),
                )
              }
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  const sectionHeader = (
    title: string,
    count: number,
    addLabel: string,
    onAdd: () => void,
  ) => (
    <div className="wb-section__header">
      <div>
        <h3>{title}</h3>
        <span>{count} item{count === 1 ? "" : "s"}</span>
      </div>
      {canCreateContent && (
        <button className="wb-btn wb-btn--primary wb-btn--small" onClick={onAdd}>
          + {addLabel}
        </button>
      )}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return <div className="wb-empty">Loading website content...</div>;
    }

    if (error) {
      return (
        <div className="wb-empty">
          <p>{error}</p>
          <button className="wb-btn wb-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    return (
      <>
        <section className="wb-section wb-section--settings">
          <div className="wb-section__header">
            <div>
              <h3>Settings</h3>
              <span>Theme, brand color and images</span>
            </div>
            {canEditContent && (
              <button
                className="wb-btn wb-btn--primary wb-btn--small"
                onClick={() => setSettingsModal(true)}
              >
                Edit Settings
              </button>
            )}
          </div>

          {settings ? (
            <div className="wb-settings">
              <div className="wb-settings__item">
                <span className="wb-settings__label">Brand color</span>
                <span className="wb-settings__value">
                  <span
                    className="wb-settings__swatch"
                    style={{ background: settings.brandColor || "#cccccc" }}
                  />
                  {settings.brandColor || "—"}
                </span>
              </div>
              <div className="wb-settings__item">
                <span className="wb-settings__label">Appointment image</span>
                <span className="wb-settings__value">
                  {settings.appointmentImageUrl ? (
                    <img
                      className="wb-settings__thumb"
                      src={resolveImageUrl(settings.appointmentImageUrl)}
                      alt="Appointment"
                    />
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="wb-settings__item">
                <span className="wb-settings__label">Tagline image</span>
                <span className="wb-settings__value">
                  {settings.taglineImageUrl ? (
                    <img
                      className="wb-settings__thumb"
                      src={resolveImageUrl(settings.taglineImageUrl)}
                      alt="Tagline"
                    />
                  ) : (
                    "—"
                  )}
                </span>
              </div>
            </div>
          ) : (
            <p className="wb-section__empty">No settings found.</p>
          )}
        </section>

        <section className="wb-section wb-section--promo">
          {sectionHeader(
            "Promo Images",
            promoImages.length,
            "Add Promo",
            () => openMediaModal("create", "promo", null),
          )}
          <div className="wb-grid">
            {promoImages.length > 0 ? (
              promoImages.map((item) => renderMediaRow(item, "promo"))
            ) : (
              <p className="wb-section__empty">No promo images yet.</p>
            )}
          </div>
        </section>

        <section className="wb-section wb-section--beforeafter">
          {sectionHeader(
            "Before / After",
            beforeAfter.length,
            "Add Before / After",
            () => openMediaModal("create", "before_after", null),
          )}
          <div className="wb-grid">
            {beforeAfter.length > 0 ? (
              beforeAfter.map((item) => renderMediaRow(item, "before_after"))
            ) : (
              <p className="wb-section__empty">No before / after items yet.</p>
            )}
          </div>
        </section>

        <section className="wb-section wb-section--stats">
          {sectionHeader("Stats", stats.length, "Add Stat", () =>
            openStatModal("create", null),
          )}
          <div className="wb-stats">
            {stats.length > 0 ? (
              stats.map((item) => (
                <div key={item.id} className="wb-stat">
                  <div className="wb-stat__body">
                  <span className="wb-stat__icon">
                    {item.icon && isStatIconImage(item.icon) ? (
                      <img
                        className="wb-stat__icon-img"
                        src={resolveImageUrl(item.icon)}
                        alt=""
                      />
                    ) : (
                      item.icon
                    )}
                  </span>
                    <div>
                      <p className="wb-stat__value">{item.value}</p>
                      <p className="wb-stat__label">{item.label}</p>
                    </div>
                  </div>
                  <div className="wb-card__actions">
                    {canEditContent && (
                      <button
                        className="wb-btn wb-btn--small"
                        onClick={() => openStatModal("edit", item)}
                      >
                        Edit
                      </button>
                    )}
                    {canDeleteContent && (
                      <button
                        className="wb-btn wb-btn--small wb-btn--danger"
                        onClick={() =>
                          confirmDelete(`Delete stat "${item.label}"?`, () =>
                            handleDeleteStat(item),
                          )
                        }
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="wb-section__empty">No stats yet.</p>
            )}
          </div>
        </section>

        <section className="wb-section wb-section--testimonials">
          {sectionHeader(
            "Testimonials",
            testimonials.length,
            "Add Testimonial",
            () => openTestimonialModal("create", null),
          )}
          <div className="wb-list">
            {testimonials.length > 0 ? (
              testimonials.map((item) => (
                <div key={item.id} className="wb-row">
                  <div className="wb-row__main">
                    <p className="wb-row__title">
                      {item.clientName}
                      {item.isFeatured && (
                        <span className="wb-badge">Featured</span>
                      )}
                    </p>
                    <p className="wb-row__sub">
                      {item.rating ? `${item.rating}★` : "No rating"}
                      {item.clientTitle ? ` · ${item.clientTitle}` : ""}
                    </p>
                    <p className="wb-row__text">{item.reviewText}</p>
                  </div>
                  <div className="wb-card__actions">
                    {canEditContent && (
                      <button
                        className="wb-btn wb-btn--small"
                        onClick={() => openTestimonialModal("edit", item)}
                      >
                        Edit
                      </button>
                    )}
                    {canDeleteContent && (
                      <button
                        className="wb-btn wb-btn--small wb-btn--danger"
                        onClick={() =>
                          confirmDelete(
                            `Delete testimonial from "${item.clientName}"?`,
                            () => handleDeleteTestimonial(item),
                          )
                        }
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="wb-section__empty">No testimonials yet.</p>
            )}
          </div>
        </section>

        {(website?.services.length ?? 0) > 0 && (
          <section className="wb-section wb-section--services">
            <div className="wb-section__header">
              <div>
                <h3>Services</h3>
                <span>{website?.services.length ?? 0} items</span>
              </div>
            </div>
            <div className="wb-list">
              {website?.services.map((item: Service) => (
                <div key={item.id} className="wb-row">
                  <div className="wb-row__main">
                    <p className="wb-row__title">{item.name}</p>
                    <p className="wb-row__text">{item.description || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </>
    );
  };

  return (
    <div className="wb-page">
      <div className="wb-header">
        <div>
          <h2>Website Builder</h2>
          <p>
            Manage the public website for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        <div className="wb-header__actions">
          <button
            className="wb-btn wb-btn--secondary"
            onClick={openPreviewWebsite}
          >
            View Website
          </button>
          <button className="wb-btn wb-btn--secondary" onClick={reload}>
            Refresh
          </button>
        </div>
      </div>

      {renderBody()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <MediaModal
        isOpen={mediaModal.open}
        mode={mediaModal.mode}
        mediaType={mediaType}
        item={mediaModal.item}
        submitting={submitting}
        onClose={() => setMediaModal((prev) => ({ ...prev, open: false }))}
        onSubmit={handleMediaSubmit}
      />

      <StatModal
        isOpen={statModal.open}
        mode={statModal.mode}
        item={statModal.item}
        submitting={submitting}
        onClose={() => setStatModal((prev) => ({ ...prev, open: false }))}
        onSubmit={handleStatSubmit}
      />

      <TestimonialModal
        isOpen={testimonialModal.open}
        mode={testimonialModal.mode}
        item={testimonialModal.item}
        submitting={submitting}
        onClose={() => setTestimonialModal((prev) => ({ ...prev, open: false }))}
        onSubmit={handleTestimonialSubmit}
      />

      <WebsiteSettingsModal
        isOpen={settingsModal}
        settings={settings}
        submitting={submitting}
        onClose={() => setSettingsModal(false)}
        onSubmit={handleSettingsSubmit}
      />
    </div>
  );
};

export default WebsiteBuilderPage;